/**
 * Phase 6 Part A: print agents, printers, and the print job queue.
 *
 * Matches `Pos.Modules.Printing.Features.PrintingEndpoints` exactly — routes, verbs, and body
 * shapes. `issuePrintAgent` is the one call in this file whose result must never be persisted or
 * logged anywhere beyond the one dialog that shows it: the raw token is returned once and the
 * server keeps only its hash from then on.
 */

import { api } from '../api.js';
import { printAgentListTag, printerListTag, printJobListTag } from '../tags.js';
import type { Paged, ShopId, VariantId } from '../../types/api.js';
import type {
  IssuedPrintAgent,
  PrintAgent,
  PrintAgentId,
  PrintJob,
  PrintJobStatus,
  Printer,
  PrinterConnection,
  PrinterId,
  PrinterType,
} from '../../types/printing.js';

export type IssuePrintAgentBody = { shopId: ShopId; name: string };
export type RevokePrintAgentBody = { id: PrintAgentId; reason: string };

export type CreatePrinterBody = {
  shopId: ShopId;
  agentId: PrintAgentId;
  name: string;
  printerType: PrinterType;
  connection: PrinterConnection;
  host?: string | undefined;
  port?: number | undefined;
  devicePath?: string | undefined;
  paperWidthMm: number;
  labelWidthMm?: number | undefined;
  labelHeightMm?: number | undefined;
  isDefault?: boolean | undefined;
};

export type UpdatePrinterBody = {
  id: PrinterId;
  name: string;
  host?: string | undefined;
  port?: number | undefined;
  devicePath?: string | undefined;
  paperWidthMm: number;
  labelWidthMm?: number | undefined;
  labelHeightMm?: number | undefined;
  isDefault?: boolean | undefined;
  isActive?: boolean | undefined;
};

/**
 * `quantity` omitted means "one label per unit currently in stock" — the server resolves that
 * itself (`PrintLabelsCommand`/`PrintLabelsHandler`), so the dashboard never fetches a stock
 * count just to pre-fill this.
 */
export type PrintLabelsBody = { variantId: VariantId; printerId: PrinterId; quantity?: number | undefined };

export type ListPrintJobsParams = {
  readonly shopId?: ShopId | undefined;
  readonly printerId?: PrinterId | undefined;
  readonly status?: PrintJobStatus | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export const printAgentApi = api.injectEndpoints({
  endpoints: (build) => ({
    listPrintAgents: build.query<PrintAgent[], { shopId?: ShopId | undefined }>({
      query: (params) => ({ url: '/print-agents', params }),
      providesTags: [printAgentListTag()],
    }),

    /** Shown exactly once by the caller — see `IssuePrintAgentDialog`. Never refetched. */
    issuePrintAgent: build.mutation<IssuedPrintAgent, IssuePrintAgentBody>({
      query: (body) => ({ url: '/print-agents', method: 'POST', body }),
      invalidatesTags: [printAgentListTag()],
    }),

    revokePrintAgent: build.mutation<PrintAgent, RevokePrintAgentBody>({
      query: ({ id, reason }) => ({ url: `/print-agents/${id}/revoke`, method: 'POST', body: { reason } }),
      invalidatesTags: [printAgentListTag(), printerListTag()],
    }),
  }),
});

export const printerApi = api.injectEndpoints({
  endpoints: (build) => ({
    listPrinters: build.query<Printer[], { shopId?: ShopId | undefined }>({
      query: (params) => ({ url: '/printers', params }),
      providesTags: [printerListTag()],
    }),

    createPrinter: build.mutation<Printer, CreatePrinterBody>({
      query: (body) => ({ url: '/printers', method: 'POST', body }),
      invalidatesTags: [printerListTag()],
    }),

    updatePrinter: build.mutation<Printer, UpdatePrinterBody>({
      query: ({ id, ...body }) => ({ url: `/printers/${id}`, method: 'PATCH', body }),
      invalidatesTags: [printerListTag()],
    }),

    /** Enqueues a test job; it does not print synchronously — see `TestPrintButton`. */
    testPrintPrinter: build.mutation<void, PrinterId>({
      query: (id) => ({ url: `/printers/${id}/test-print`, method: 'POST' }),
      invalidatesTags: [printJobListTag()],
    }),
  }),
});

export const printJobApi = api.injectEndpoints({
  endpoints: (build) => ({
    listPrintJobs: build.query<Paged<PrintJob>, ListPrintJobsParams>({
      query: (params) => ({ url: '/print-jobs', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [printJobListTag()],
    }),

    retryPrintJob: build.mutation<PrintJob, PrintJob['id']>({
      query: (id) => ({ url: `/print-jobs/${id}/retry`, method: 'POST' }),
      invalidatesTags: [printJobListTag()],
    }),

    cancelPrintJob: build.mutation<PrintJob, PrintJob['id']>({
      query: (id) => ({ url: `/print-jobs/${id}/cancel`, method: 'POST' }),
      invalidatesTags: [printJobListTag()],
    }),

    /**
     * Direct-to-label-printer printing, alongside `exportLabelSheet`'s PDF path (see
     * `Labels/Features/LabelFeatures.cs`). Queues a `label_sheet` job the same way a receipt does
     * — it does not print synchronously, so the caller shows "queued", never "printed".
     */
    printLabels: build.mutation<PrintJob, PrintLabelsBody>({
      query: (body) => ({ url: '/labels/print-job', method: 'POST', body }),
      invalidatesTags: [printJobListTag()],
    }),
  }),
});

export const {
  useListPrintAgentsQuery,
  useIssuePrintAgentMutation,
  useRevokePrintAgentMutation,
} = printAgentApi;

export const {
  useListPrintersQuery,
  useCreatePrinterMutation,
  useUpdatePrinterMutation,
  useTestPrintPrinterMutation,
} = printerApi;

export const {
  useListPrintJobsQuery,
  useRetryPrintJobMutation,
  useCancelPrintJobMutation,
  usePrintLabelsMutation,
} = printJobApi;
